import { Form, type FormInstance } from 'antd';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { Span } from '@/components/Typography/Typo';
import { modalStyle } from '@/styles/style';
import type { SOURCE_TYPE } from '@/types/feedbackConst';

import {
  getIsItemPerDayField,
  getMaxItemCountByScraper,
  getScraperFieldList,
} from '../../configs/config-actions';
import {
  type DraftScrapingJob,
  getScrapingJobTypeIcon,
  type SCRAPER_NAME,
  SCRAPING_JOB_TYPE,
  SCRAPING_SORT,
} from '../../types';
import { FieldMapper } from '../FieldMapper';
import { AdminConfirmField } from '../Fields/AdminConfirmField';
import { ItemPerDayField } from '../Fields/ItemPerDayField';
import { MaxItemsField } from '../Fields/MaxItemsField';
import { ScheduleField } from '../Fields/ScheduleField';
import { ScraperOptionField } from '../Fields/ScraperOptionField';
import { SourceTypeField } from '../Fields/SourceTypeField';
import { TitleField } from '../Fields/TitleField';
import { GuideBannerBySource } from '../GuideBannerBySource';

type Props = {
  form: FormInstance<DraftScrapingJob>;
  jobType: SCRAPING_JOB_TYPE;
  sourceOptions: { label: ReactNode; value: SOURCE_TYPE }[];
  scraperOptions: { label: ReactNode; value: SCRAPER_NAME }[];
  currentScraperOption?: SCRAPER_NAME;
  loading?: boolean;
  onSubmit: (values: DraftScrapingJob) => void;
  onClose?: () => void;
  initialValues?: Partial<DraftScrapingJob>;
};

const DEFAULT_INITIAL_VALUES: Partial<DraftScrapingJob> = {
  title: '',
  // type is set by consumer or left unset for UI-only usage
  // source_type and scraper_name are provided via initialValues from consumer
  username: '',
  max_items: undefined,
  product_line: '',
  brand: '',
  product_name: '',
  page_url: '',
  group_alias: '',
  cafe_id: undefined,
  cafe_name: '',
  exact_match: false,
  sort: SCRAPING_SORT.RECENT,
  link: '',
  definition: '',
  links: [],
  keyword: '',
  keywords: [],
  include_keywords: undefined,
  exclude_keywords: undefined,
  need_confirm: false,
  subreddit: undefined,
  shift_days: undefined,
  all_options: true,
};

export const CreateScrapingJobForm = (props: Props) => {
  const {
    form,
    jobType,
    sourceOptions,
    scraperOptions,
    currentScraperOption,
    loading,
    onSubmit,
    onClose,
    initialValues,
  } = props;

  const scraperFieldList = currentScraperOption
    ? getScraperFieldList(currentScraperOption)
    : [];

  const isItemPerDay = getIsItemPerDayField(currentScraperOption);

  const JobIcon = getScrapingJobTypeIcon(jobType);
  const sourceType = Form.useWatch('source_type', form);

  return (
    <Style>
      <Flex a="center" g={8}>
        <JobIcon size={20} />
        <Span size={0} weight="sb">
          {jobType === SCRAPING_JOB_TYPE.ONETIME
            ? 'Create job'
            : 'Create schedule'}
        </Span>
      </Flex>
      <Space h={32} />
      <Form
        form={form}
        onFinish={onSubmit}
        layout="horizontal"
        labelAlign="left"
        labelCol={{ prefixCls: 'w-[140px]' }}
        wrapperCol={{ flex: 1 }}
        initialValues={{
          ...DEFAULT_INITIAL_VALUES,
          ...(initialValues || {}),
        }}
      >
        <VFlex>
          <TitleField />
          <SourceTypeField sourceOptions={sourceOptions} />
          <GuideBannerBySource sourceType={sourceType} />
          <ScraperOptionField scraperOptions={scraperOptions} />
          {currentScraperOption &&
            scraperFieldList.map((field) => (
              <FieldMapper
                key={field}
                scraping_field={field}
                scraper_name={currentScraperOption}
              />
            ))}
          <Divider className="my-4" />
          <ScheduleField type={jobType} />

          {isItemPerDay ? (
            <ItemPerDayField
              maxCount={getMaxItemCountByScraper(currentScraperOption)}
            />
          ) : (
            <MaxItemsField
              maxCount={getMaxItemCountByScraper(currentScraperOption)}
            />
          )}
          <AdminConfirmField />
        </VFlex>

        <Flex j="flex-end" a="center" g={12}>
          <Button sizeType="m" variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button
            sizeType="m"
            variant="primary"
            type="submit"
            isLoading={!!loading}
          >
            Create
          </Button>
        </Flex>
      </Form>
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 600px;
  padding: 32px;
  overflow: auto;
  max-height: calc(100vh - 200px);
  ${modalStyle};
  .ant-form-item-explain-error {
    font-size: 12px;
  }
`;

export default CreateScrapingJobForm;
